import {
  OUT_OF_STOCK_URGENCY_LABELS,
  STOCK_WAITING_RECENT_DAYS,
  waitingFor,
  type OutOfStockProduct,
  type OutOfStockUrgency,
  type StockWaitingList,
  type StockWaitingProduct,
  type StockWaitingRetry,
} from '@wms/domain';
import { useState } from 'react';
import { api, errorMessage } from '../api';
import { fmt } from '../format';
import { useSession } from '../session';
import { marketplaceText, receivingPath, stockWaitingHints, type StockWaitingHint } from '../stockWaiting';
import { ChannelBadge, Empty, ErrorBanner, ProductThumb, SellerChip, Spinner, useLoad, useToast } from '../ui';
import { Icon } from '../ui/icons';

const HINT_TONE: Readonly<Record<StockWaitingHint['tone'], string>> = { ok: 'banner ok', warn: 'banner warn', info: 'banner info-soft' };
const URGENCY_CHIP: Readonly<Record<OutOfStockUrgency, string>> = { high: 'chip bad', medium: 'chip warn', low: 'chip' };

/**
 * Stok bekleyen siparişler: the orders waiting for stock, product by product, with what may bring it
 * (goods at the dock, an open shipment, a marketplace that shows stock); below, the products the
 * warehouse has none of that sell, are on the way or show stock somewhere, before an order waits.
 */
export function StockWaiting({ onChange }: { onChange: () => void }) {
  const { warehouseId, isSupervisor } = useSession();
  const toast = useToast();
  const list = useLoad(
    () => (warehouseId ? api<StockWaitingList>('GET', `/api/wms/stock-waiting?warehouseId=${warehouseId}`) : Promise.resolve(undefined)),
    [warehouseId],
    30_000,
  );
  const [busy, setBusy] = useState<string | null>(null);

  const retry = async (p: StockWaitingProduct) => {
    if (!warehouseId) return;
    setBusy(p.productId);
    try {
      const outcome = await api<StockWaitingRetry>('POST', '/api/wms/stock-waiting/retry', { warehouseId, productId: p.productId });
      const still = outcome.waiting.length > 0 ? `${fmt.n(outcome.waiting.length)} sipariş hâlâ stok bekliyor` : '';
      toast(
        outcome.allocated.length > 0
          ? `${outcome.allocated.join(', ')} toplamaya hazır${still ? `; ${still}` : ''}`
          : still
            ? `${p.sku}: stok yetmiyor, ${still}`
            : `${p.sku}: stok bekleyen sipariş kalmadı`,
        outcome.allocated.length > 0 ? 'ok' : 'info',
      );
      void list.reload();
      onChange();
    } catch (err) {
      toast(errorMessage(err), 'bad');
    } finally {
      setBusy(null);
    }
  };

  if (!warehouseId) return <Empty title="Depo seçilmedi" />;
  if (list.error) return <ErrorBanner message={list.error} onRetry={() => void list.reload()} />;
  if (!list.data) return <Spinner />;
  const { products, outOfStock, orders } = list.data;
  const now = new Date();

  return (
    <>
      {products.length === 0 ? (
        <section className="card">
          <Empty title="Stok bekleyen sipariş yok">Stoğu olmayan ürünü isteyen bir sipariş gelince burada ürün ürün listelenir; depo da bildirim alır.</Empty>
        </section>
      ) : (
        <div className="banner warn">
          <Icon name="clock" />
          <div style={{ flex: 1 }}>
            {fmt.n(orders)} sipariş {fmt.n(products.length)} ürünü bekliyor.
            <div style={{ fontWeight: 500 }}>
              Ürün depodaysa mal kabulden stoğa girip rafa koyun; değilse satıcının gönderisi bekleniyor. Rafa giren ürün siparişlere kendiliğinden ayrılır.
            </div>
          </div>
        </div>
      )}
      {products.map((p) => (
        <section key={p.productId} className="card">
          <div className="card-h">
            <ProductThumb name={p.name} imageUrl={p.imageUrl} small />
            <h2>
              <span className="mono">{p.sku}</span> · {p.name}
            </h2>
            <SellerChip code={p.tenantCode} />
            <span className="sub">
              {fmt.n(p.orders.length)} sipariş · {p.orders.length > 1 ? 'en eskisi ' : ''}{waitingFor(p.waitingSince, now)}
            </span>
            <span className="spacer" />
            {isSupervisor && (
              <button className="btn" disabled={busy === p.productId} onClick={() => void retry(p)} title="Bekleyen siparişleri yeniden ayır">
                <Icon name="refresh" size={16} /> Yeniden dene
              </button>
            )}
            <a className="btn primary" href={`#${receivingPath(p.inbound)}`}>
              <Icon name="inbox" size={16} /> Mal kabul
            </a>
          </div>
          <div className="card-b stack">
            <div className="row">
              <span className={`chip ${p.missing > 0 ? 'bad' : 'ok'}`}>{p.missing > 0 ? `${fmt.n(p.missing)} adet eksik` : 'Stok yetiyor'}</span>
              <span className="muted">
                Siparişlere gereken {fmt.n(p.needed)} · depoda {fmt.n(p.onHand)} · kullanılabilir {fmt.n(p.available)}
                {p.atDock > 0 ? ` · kabul alanında ${fmt.n(p.atDock)}` : ''}
                {p.toShelf > 0 ? ` · rafa dönecek ${fmt.n(p.toShelf)}` : ''}
              </span>
            </div>
            {stockWaitingHints(p).map((hint, i) => (
              <div key={i} className={HINT_TONE[hint.tone]}>
                <div style={{ flex: 1, fontWeight: 500 }}>{hint.text}</div>
                {hint.link && (
                  <a className="btn sm" href={`#${hint.link.to}`}>
                    {hint.link.label}
                  </a>
                )}
              </div>
            ))}
            <div className="table-wrap">
              <table className="t stack-sm">
                <thead>
                  <tr>
                    <th>Sipariş</th>
                    <th>Kanal</th>
                    <th className="num">Adet</th>
                    <th>Bekliyor</th>
                    <th>Sipariş tarihi</th>
                    <th>Son kargo</th>
                  </tr>
                </thead>
                <tbody>
                  {p.orders.map((o) => (
                    <tr key={o.orderId}>
                      <td>
                        <a className="mono" href={`#/siparisler?q=${encodeURIComponent(o.orderNumber)}`}>
                          {o.orderNumber}
                        </a>
                      </td>
                      <td data-label="Kanal">
                        <ChannelBadge channel={o.channel} />
                      </td>
                      <td className="num" data-label="Adet">
                        {fmt.n(o.qty)}
                      </td>
                      <td data-label="Bekliyor">{waitingFor(o.waitingSince, now)}</td>
                      <td data-label="Sipariş">{fmt.dateTime(o.orderedAt)}</td>
                      <td data-label="Son kargo">{o.shipBy ? fmt.dateTime(o.shipBy) : '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      ))}
      <OutOfStock products={outOfStock} />
    </>
  );
}

/** "Stoğu olmayan ürünler": no unit in the warehouse, yet ordered lately, on the way or shown in stock on a marketplace. */
function OutOfStock({ products }: { products: readonly OutOfStockProduct[] }) {
  return (
    <section className="card">
      <div className="card-h">
        <Icon name="stock" />
        <h2>Stoğu olmayan ürünler</h2>
        <span className="sub">
          Depoda hiç adedi yok; son {STOCK_WAITING_RECENT_DAYS} günde sipariş aldı, yolda ya da pazaryerinde stokta görünüyor. Ürün depodaysa sipariş beklemeden stoğa girin.
        </span>
      </div>
      {products.length === 0 ? (
        <Empty title="Bu durumda ürün yok" />
      ) : (
        <div className="table-wrap">
          <table className="t stack-sm">
            <thead>
              <tr>
                <th>Ürün</th>
                <th>Durum</th>
                <th className="num">Son {STOCK_WAITING_RECENT_DAYS} gün</th>
                <th>Yoldaki gönderi</th>
                <th>Pazaryeri</th>
              </tr>
            </thead>
            <tbody>
              {products.map((p) => (
                <tr key={p.productId}>
                  <td>
                    <span className="mono">{p.sku}</span> <span className="muted">{p.name}</span> <SellerChip code={p.tenantCode} />
                  </td>
                  <td data-label="Durum">
                    <span className={URGENCY_CHIP[p.urgency]}>{OUT_OF_STOCK_URGENCY_LABELS[p.urgency]}</span>
                  </td>
                  <td className="num" data-label={`Son ${STOCK_WAITING_RECENT_DAYS} gün`}>
                    {p.recentOrders > 0 ? `${fmt.n(p.recentOrders)} sipariş` : '—'}
                  </td>
                  <td data-label="Yoldaki gönderi">
                    {p.inbound.length === 0
                      ? '—'
                      : p.inbound.map((s) => (
                          <a key={s.shipmentId} className="mono" href={`#/kabul/${encodeURIComponent(s.asnNumber)}`} style={{ marginRight: 8 }}>
                            {s.asnNumber} · {fmt.n(s.remaining)} adet
                          </a>
                        ))}
                  </td>
                  <td data-label="Pazaryeri">{p.marketplace ? marketplaceText(p.marketplace) : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
